import React, { useEffect, useRef, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import {
  LayoutDashboard, Calendar, Users, MessageSquare, BarChart2,
  BellRing, Settings, ChevronDown, HelpCircle, Globe,
  User, LogOut, KeyRound, ClipboardList,
  Stethoscope, Layers, Wallet, ClipboardPlus, Receipt, FileText,
  Scan, Mic, BarChart3, Inbox, CalendarDays, Menu, X, FlaskConical,
  ClipboardCheck, PackageSearch, PackagePlus, Wrench, ListChecks, ShieldCheck, Send, Bot, HeartHandshake, Activity} from 'lucide-react'
import { cn } from '../lib/utils'
import GlobalSearch from '../components/GlobalSearch'
import ReceptionMotivator from '../components/ReceptionMotivator'
import GlobalShortcuts from '../components/GlobalShortcuts'
import NotificationBell from '../components/NotificationBell'
import { useLanguage } from '../lib/LanguageContext'

const NAV_ITEMS = [
  { key: 'nav_dashboard',     href: '/dashboard',     icon: LayoutDashboard },
  { key: 'nav_diary',         href: '/diary',         icon: CalendarDays },
  { key: 'nav_reminders',     href: '/reminders',     icon: BellRing },
  { key: 'nav_patients',      href: '/patients',      icon: Users },
  { key: 'nav_conversations', href: '/conversations', icon: MessageSquare },
  { key: 'nav_analytics',     href: '/analytics',     icon: BarChart2 },
  { key: 'nav_audit_log',    href: '/audit-log',     icon: ClipboardList },
  { key: 'nav_ai_change_log', href: '/ai-change-log', icon: Bot, label: 'AI Change Log' },
  { key: 'nav_receptionist', href: '/receptionist', icon: ShieldCheck, label: 'Receptionist' },
  { key: 'nav_chart_shadow', href: '/chart-shadow', icon: Activity, label: 'Chart Shadow' },
]

// Practice-management system (Phase 1+). Literal labels for now — localisation parked.
//
// Patient-as-spine: Accounts, Courses of Treatment, Estimates and Invoices are
// NOT top-level headings — they are per-patient records and live as tabs inside
// each patient (PatientShow). You reach a treatment plan / estimate / invoice by
// opening the patient, never by browsing a separate document-type list. What
// remains here are genuine practice-WIDE tools, not per-patient record types.
const PMS_NAV_ITEMS = [
  { label: 'Review queue',         href: '/patients/review_queue', icon: ClipboardPlus },
  { label: 'Mailbox',              href: '/mail',                 icon: Inbox },
  { label: 'Imaging (SIDEXIS)',    href: '/imaging',              icon: Scan },
  { label: 'Chair-side Scribe',    href: '/scribe-sessions',      icon: Mic },
  { label: 'Recalls',              href: '/recalls',              icon: BellRing },
  { label: 'Estimates tracker',    href: '/estimates',            icon: FileText },
  { label: 'Case acceptance',      href: '/case-acceptance',      icon: BarChart2 },
  { label: 'Lab cases',            href: '/lab',                  icon: FlaskConical },
  { label: 'Referrals',            href: '/referrals',            icon: Send },
  { label: 'Reporting',            href: '/reporting',            icon: BarChart3 },
  { label: 'Transaction report',   href: '/reporting/transactions', icon: Receipt },
  { label: 'Procedure Codes',      href: '/procedure-codes',      icon: Stethoscope },
  { label: 'Treatment Macros',     href: '/treatment-macros',     icon: Layers },
]

export default function DashboardLayout({ children }) {
  const { url, props } = usePage()
  const { t, language, setLanguage } = useLanguage()
  // Stock Take nav (feature-flagged); Catalogue + Pending items are owner-only.
  const stockNav = props?.stock_nav || { enabled: false, owner: false }
  // Read-only "viewer" staff see ONLY the diary (server also enforces this — see enforce_viewer_scope!).
  const role = props?.current_user?.role
  const isViewer = role === 'viewer'
  const isAdmin = role === 'admin'
  const isDentist = role === 'dentist'
  // The finance house (Expo Finance) sees ONLY its "Finance applications" tab — the server enforces the same
  // (ApplicationController#enforce_lender_scope!). No patient search, bell, shortcuts or reception popups.
  const isLender = role === 'lender'
  // The account chip reads the ACTUAL logged-in user (was hardcoded "DL"/"Dr le Roux"/"Dentist").
  const cu = props?.current_user
  const displayName = cu?.name || 'User'
  const displayRole = isLender ? 'Finance house' : (role ? role.charAt(0).toUpperCase() + role.slice(1) : '')
  const initials =
    displayName
      .replace(/\(.*?\)/g, ' ')                       // drop "(view-only)" etc.
      .split(/[\s—–-]+/)
      .filter((w) => /[A-Za-z]/.test(w))
      .filter((w) => !/^dr\.?$/i.test(w))             // skip the "Dr" honorific
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || displayName[0]?.toUpperCase() || 'U'
  // Owner-only destinations (also HARD-blocked server-side in enforce_owner_only_scope!): financials,
  // audit, settings, and the chair-side-scribe transcript.
  const ADMIN_ONLY = ['/analytics', '/audit-log', '/reporting', '/reporting/transactions', '/scribe-sessions', '/settings', '/receptionist']
  // A dentist's clinical toolkit — everything not on this list is hidden for the dentist.
  const DENTIST_NAV = ['/diary', '/patients', '/recalls', '/lab', '/imaging', '/procedure-codes', '/treatment-macros', '/reminders', '/service-schedule']
  const visible = (href) => {
    if (isAdmin) return true
    if (ADMIN_ONLY.includes(href)) return false
    if (isDentist) return DENTIST_NAV.includes(href)
    return true // reception: everything except owner-only
  }
  // Private patient feedback — the ONE nav item that is not role-driven. The patient is promised a
  // single named reader, so the tab renders only for the practice manager (server prop; the page
  // 404s independently). Never add '/feedback' to ADMIN_ONLY — a second admin must NOT inherit it.
  const feedbackVisible = !!props?.feedback_nav?.visible
  const navItems = isViewer
    ? NAV_ITEMS.filter((i) => i.href === '/diary')
    : isLender
    ? [{ key: 'nav_finance', href: '/finance-review', icon: Wallet, label: 'Finance applications' }]
    : [
        ...NAV_ITEMS.filter((i) => visible(i.href)),
        ...(feedbackVisible
          ? [{ key: 'nav_feedback', href: '/feedback', icon: HeartHandshake, label: 'Feedback' }]
          : []),
      ]
  // Monthly equipment-maintenance checklist — red dot until this month is fully signed off.
  const serviceDue = !!props?.service_schedule_due
  // Reception accountability — count of things overdue right now (drives the red badge on the tab).
  const oversightRed = props?.oversight?.red_count || 0
  const pmsItems = (isViewer || isLender) ? [] : [
    ...PMS_NAV_ITEMS.filter((i) => visible(i.href)),
    // Patient finance applications — the practice OWNER only (server: ADMIN_ONLY_PATHS). Paul 2026-09-30.
    ...(isAdmin ? [{ label: 'Finance applications', href: '/finance-review', icon: Wallet }] : []),
    { label: 'Service schedule', href: '/service-schedule', icon: Wrench },
    ...(stockNav.enabled ? [{ label: 'Stock Take', href: '/stock_takes', icon: ClipboardCheck }] : []),
    ...(stockNav.enabled && stockNav.owner ? [
      { label: 'Stock Catalogue', href: '/stock/catalogue', icon: PackageSearch },
      { label: 'Pending Items',   href: '/stock/pending',   icon: PackagePlus },
    ] : []),
  ]
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileNav, setMobileNav] = useState(false)
  // Close the mobile nav drawer whenever we navigate to a new page.
  useEffect(() => router.on('navigate', () => setMobileNav(false)), [])
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const isActive = (href) =>
    href === '/dashboard'
      ? url === '/' || url === '/dashboard'
      : url.startsWith(href)

  return (
    <div className="min-h-screen bg-brand-surface text-brand-ink">
      {/* R4 — Practice-wide keyboard shortcuts */}
      {!isLender && <GlobalShortcuts />}

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      {mobileNav && <div className="fixed inset-0 z-20 bg-black/40 lg:hidden" onClick={() => setMobileNav(false)} aria-hidden="true" />}
      <aside className={cn("fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-brand-border bg-white transition-transform lg:translate-x-0", mobileNav ? "translate-x-0" : "-translate-x-full")}>

        {/* Close button — mobile drawer only */}
        <button onClick={() => setMobileNav(false)} className="absolute right-2 top-2 rounded-lg p-2 text-brand-muted hover:bg-brand-surface lg:hidden" aria-label="Close menu">
          <X size={18} />
        </button>

        {/* Practice identity — h-16 matches the top navbar height exactly */}
        <div className="flex h-16 flex-shrink-0 items-center gap-3 border-b border-brand-border px-5">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-brand-primary">
              <span className="select-none text-sm font-bold text-white">DL</span>
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-sm font-semibold leading-tight text-brand-ink">
                Dr Chalita le Roux
              </h1>
              <p className="mt-0.5 text-xs tracking-wide text-brand-muted">{t('nav_subtitle')}</p>
            </div>
        </div>

        {/* Main navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-muted select-none">
            {t('nav_menu')}
          </p>
          {navItems.map(({ key, href, icon: Icon, label }) => {
            const active = isActive(href)
            // Reception accountability tab lights red with a live count when something's overdue.
            const alert = href === '/receptionist' && oversightRed > 0
            return (
              <Link
                key={key}
                href={href}
                className={cn(
                  'flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-all',
                  active
                    ? 'bg-brand-primary text-white'
                    : alert
                      ? 'bg-brand-danger/10 text-brand-danger hover:bg-brand-danger/15'
                      : 'text-brand-muted hover:bg-brand-surface hover:text-brand-ink'
                )}
              >
                <Icon
                  size={16}
                  className={cn('flex-shrink-0', active ? 'text-white' : alert ? 'text-brand-danger' : 'text-brand-muted')}
                />
                <span className="truncate">{label || t(key)}</span>
                {alert ? (
                  <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-danger px-1.5 text-[11px] font-bold text-white" title="Overdue items">{oversightRed}</span>
                ) : active ? (
                  <span className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white" />
                ) : null}
              </Link>
            )
          })}

          {/* Practice-management section (Phase 1+) — no heading when there is nothing under it */}
          {pmsItems.length > 0 && (
          <p className="mb-3 mt-6 px-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-muted select-none">
            Practice
          </p>
          )}
          {pmsItems.map(({ label, href, icon: Icon }) => {
            const active = isActive(href)
            const needsAttention = href === '/service-schedule' && serviceDue
            return (
              <Link
                key={href}
                href={href}
                className={cn(
                  'flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-all',
                  active
                    ? 'bg-brand-primary text-white'
                    : needsAttention
                      ? 'bg-brand-danger/10 text-brand-danger hover:bg-brand-danger/15'
                      : 'text-brand-muted hover:bg-brand-surface hover:text-brand-ink'
                )}
              >
                <Icon size={16} className={cn('flex-shrink-0', active ? 'text-white' : needsAttention ? 'text-brand-danger' : 'text-brand-muted')} />
                <span className="truncate">{label}</span>
                {active
                  ? <span className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white" />
                  : needsAttention
                    ? <span className="ml-auto h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full bg-brand-danger" title="Due this month" />
                    : null}
              </Link>
            )
          })}
        </nav>

        {/* Bottom — Settings / Support */}
        <div className="space-y-1 border-t border-brand-border px-3 pb-4 pt-3">
          {isAdmin && (
          <Link
            href="/settings"
            className={cn(
              'flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-all',
              isActive('/settings')
                ? 'bg-brand-primary text-white'
                : 'text-brand-muted hover:bg-brand-surface hover:text-brand-ink'
            )}
          >
            <Settings
              size={16}
              className={cn(
                'flex-shrink-0',
                isActive('/settings') ? 'text-white' : 'text-brand-muted'
              )}
            />
            {t('nav_settings')}
            {isActive('/settings') && (
              <span className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white" />
            )}
          </Link>
          )}

          {!isLender && (
          <button className="w-full rounded-xl px-3.5 py-3 text-left text-sm font-medium text-brand-muted transition-all hover:bg-brand-surface hover:text-brand-ink">
            <span className="flex items-center gap-3">
              <HelpCircle size={16} className="flex-shrink-0 text-brand-muted" />
              {t('nav_support')}
            </span>
          </button>
          )}
        </div>
      </aside>

      {/* ── Top navbar ──────────────────────────────────────────────── */}
      <header className="fixed inset-x-0 top-0 z-20 flex h-16 items-center gap-4 border-b border-brand-border bg-white pl-4 pr-6 lg:pl-64">
        <button onClick={() => setMobileNav(true)} className="-ml-1 rounded-lg p-2 text-brand-muted hover:bg-brand-surface lg:hidden" aria-label="Open menu">
          <Menu size={20} />
        </button>

        {/* Left spacer — keeps the search visually centered */}
        <div className="flex-1" />

        {/* Search — centered, wider, functional */}
        {!isLender && <GlobalSearch />}

        {/* Right side — language toggle + bell + doctor */}
        <div className="flex-1 flex items-center justify-end gap-2">
          {/* Quick language toggle — EN / AF */}
          <div className="flex items-center gap-1 rounded-lg border border-brand-border bg-brand-surface px-1 py-0.5">
            <Globe size={13} className="text-brand-muted ml-1" />
            {['en', 'af'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={cn(
                  'rounded px-2 py-0.5 text-xs font-semibold transition-colors',
                  language === lang
                    ? 'bg-white text-brand-ink shadow-sm'
                    : 'text-brand-muted hover:text-brand-ink'
                )}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          {!isLender && <NotificationBell />}

          <div className="mx-1 h-6 w-px bg-brand-border" />

          {/* Doctor dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="group flex items-center gap-2 rounded-xl border border-transparent py-1.5 pl-2 pr-2 transition-colors hover:border-brand-border hover:bg-brand-surface"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary">
                <span className="text-white text-xs font-semibold select-none">{initials}</span>
              </div>
              <span className="hidden whitespace-nowrap text-sm font-medium text-brand-ink sm:block">
                {displayName}
              </span>
              <ChevronDown
                size={14}
                className={cn('text-brand-muted transition-transform group-hover:text-brand-ink', dropdownOpen && 'rotate-180')}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full z-50 mt-1.5 w-52 rounded-xl border border-brand-border bg-white py-1.5 shadow-[0_24px_60px_-16px_rgba(57,60,77,0.25)]">
                <div className="border-b border-brand-border px-4 py-2.5">
                  <p className="text-xs font-semibold text-brand-ink">{displayName}</p>
                  <p className="mt-0.5 text-[11px] text-brand-muted">{displayRole}</p>
                </div>
                {isAdmin && (
                <div className="py-1">
                  <DropdownItem
                    icon={User}
                    label="My Profile"
                    onClick={() => { setDropdownOpen(false); router.visit('/settings') }}
                  />
                </div>
                )}
                {/* Change Password is for ANY logged-in user (not admin-only) — points at the real page. */}
                <div className="py-1">
                  <DropdownItem
                    icon={KeyRound}
                    label="Change Password"
                    onClick={() => { setDropdownOpen(false); router.visit('/account/password') }}
                  />
                </div>
                <div className="border-t border-brand-border py-1">
                  <DropdownItem
                    icon={LogOut}
                    label="Sign out"
                    danger
                    onClick={() => {
                      setDropdownOpen(false)
                      router.delete('/logout', { onError: () => router.visit('/') })
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Page content ─────────────────────────────────────────── */}
      {/* Outer gutter scales with screen: 16px on a phone (maximise content width) → 32px on desktop. */}
      <main className="min-h-screen pt-16 lg:ml-64">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Dismissable receptionist progress popup — reception/admin only (not viewers/dentists). */}
          {!isViewer && !isDentist && !isLender && <ReceptionMotivator />}
          {children}
        </div>
      </main>

    </div>
  )
}

function DropdownItem({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors',
        danger
          ? 'text-brand-danger hover:bg-brand-danger/5'
          : 'text-brand-ink hover:bg-brand-surface'
      )}
    >
      <Icon size={15} className={danger ? 'text-brand-danger' : 'text-brand-muted'} />
      {label}
    </button>
  )
}
